'use client';

import { Trash2 } from 'lucide-react';
import * as React from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SetupExperienceVariantImageUpload } from '@/components/setup/experience/SetupExperienceVariantImageUpload';
import { SETUP_SOFT_INPUT_CLASS } from '@/components/setup/experience/SetupStackedFormField';
import type { SetupManualProductsFormData } from '@/components/setup/setupManualSchemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface SetupProductVariantCardProps {
  productIndex: number;
  variantIndex: number;
  currency: string;
  canRemove: boolean;
  onRemove: (variantIndex: number) => void;
}

/** Flat variant card — image, name, price and SKU all visible, no accordion. */
export const SetupProductVariantCard = React.memo(function SetupProductVariantCard({
  productIndex,
  variantIndex,
  currency,
  canRemove,
  onRemove,
}: SetupProductVariantCardProps) {
  const { t } = useTranslation('setup');
  const f = 'experience.connect.productForm';
  const { control } = useFormContext<SetupManualProductsFormData>();
  const base = `products.${productIndex}.variants.${variantIndex}` as const;
  // Leaf-name subscription — only this variant's error slice triggers a re-render.
  const { errors } = useFormState({
    control,
    name: [`${base}.name`, `${base}.imageUrl`, `${base}.priceAmount`, `${base}.sku`],
  });

  const variantErrors = errors.products?.[productIndex]?.variants?.[variantIndex];
  const fieldId = `setup-product-${productIndex}-variant-${variantIndex}`;

  return (
    <li className="flex items-start gap-4 rounded-xl border border-[color-mix(in_srgb,var(--border)_85%,transparent)] bg-[color-mix(in_srgb,var(--muted)_18%,var(--card))] p-4">
      <Controller
        name={`${base}.imageUrl`}
        control={control}
        render={({ field }) => (
          <div className="shrink-0">
            <SetupExperienceVariantImageUpload
              id={`${fieldId}-image`}
              url={field.value ?? ''}
              onImageAvailable={(nextUrl) => field.onChange(nextUrl || '')}
              uploadingLabel={t('steps.storeIntegration.manual.productStep.uploadingImage')}
              uploadingHint={t('steps.storeIntegration.manual.productStep.uploadingImageHint')}
              changeLabel={t('experience.connect.manualProduct.changeImage')}
              uploadLabel={t('steps.storeIntegration.manual.productStep.uploadImage')}
              removeLabel={t('steps.storeIntegration.manual.productStep.removeImage')}
              invalidTypeMessage={t('steps.storeIntegration.manual.productStep.uploadImageInvalidType')}
              invalid={!!variantErrors?.imageUrl}
              describedBy={variantErrors?.imageUrl ? `${fieldId}-image-error` : undefined}
            />
            {variantErrors?.imageUrl?.message ? (
              <p id={`${fieldId}-image-error`} className="text-destructive m-0 mt-1.5 text-xs" role="alert">
                {variantErrors.imageUrl.message}
              </p>
            ) : null}
          </div>
        )}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <div>
          <label className="sr-only" htmlFor={`${fieldId}-name`}>
            {t(`${f}.variantNameLabel`)}
          </label>
          <Controller
            name={`${base}.name`}
            control={control}
            render={({ field }) => (
              <Input
                id={`${fieldId}-name`}
                {...field}
                value={field.value || ''}
                placeholder={t(`${f}.variantNamePlaceholder`)}
                className={cn(SETUP_SOFT_INPUT_CLASS, 'h-11 px-3.5')}
                aria-required
                aria-invalid={!!variantErrors?.name}
                aria-describedby={variantErrors?.name ? `${fieldId}-name-error` : undefined}
              />
            )}
          />
          {variantErrors?.name?.message ? (
            <p id={`${fieldId}-name-error`} className="text-destructive m-0 mt-1.5 text-xs" role="alert">
              {variantErrors.name.message}
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-[minmax(8rem,11rem)_1fr] gap-2.5 max-[480px]:grid-cols-1">
          <div>
            <label className="sr-only" htmlFor={`${fieldId}-price`}>
              {`${t(`${f}.variantPriceLabel`)} (${currency})`}
            </label>
            <div
              className={cn(
                'flex h-11 items-center overflow-hidden rounded-xl border border-[color-mix(in_srgb,var(--border)_85%,transparent)] bg-[color-mix(in_srgb,var(--muted)_40%,var(--card))] focus-within:ring-1 focus-within:ring-[var(--ring)]',
                variantErrors?.priceAmount && 'border-destructive',
              )}
            >
              <span
                className="text-muted-foreground flex h-full shrink-0 items-center border-r border-[color-mix(in_srgb,var(--border)_85%,transparent)] px-2.5 text-xs font-semibold tracking-[0.04em]"
                aria-hidden
              >
                {currency}
              </span>
              <Controller
                name={`${base}.priceAmount`}
                control={control}
                render={({ field }) => (
                  <Input
                    id={`${fieldId}-price`}
                    type="text"
                    inputMode="decimal"
                    {...field}
                    value={field.value || ''}
                    placeholder={t(`${f}.variantPricePlaceholder`)}
                    className="h-full border-0 bg-transparent text-sm shadow-none focus-visible:ring-0"
                    aria-required
                    aria-invalid={!!variantErrors?.priceAmount}
                    aria-describedby={variantErrors?.priceAmount ? `${fieldId}-price-error` : undefined}
                  />
                )}
              />
            </div>
            {variantErrors?.priceAmount?.message ? (
              <p id={`${fieldId}-price-error`} className="text-destructive m-0 mt-1.5 text-xs" role="alert">
                {variantErrors.priceAmount.message}
              </p>
            ) : null}
          </div>

          <div>
            <label className="sr-only" htmlFor={`${fieldId}-sku`}>
              {t(`${f}.variantSkuLabel`)}
            </label>
            <Controller
              name={`${base}.sku`}
              control={control}
              render={({ field }) => (
                <Input
                  id={`${fieldId}-sku`}
                  {...field}
                  value={field.value || ''}
                  placeholder={t(`${f}.variantSkuPlaceholder`)}
                  className={cn(SETUP_SOFT_INPUT_CLASS, 'h-11 px-3.5')}
                />
              )}
            />
          </div>
        </div>
      </div>

      {canRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive size-8 shrink-0"
          onClick={() => onRemove(variantIndex)}
          aria-label={t(`${f}.removeVariant`)}
        >
          <Trash2 className="size-4" aria-hidden />
        </Button>
      ) : null}
    </li>
  );
});
